import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Spin, Button, Tabs } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { PageHeaderCustom } from '../../../components/PageHeader/PageHeader';
import { ContentCustom } from '../../../components';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useErrorMessage } from '../../../utils/errorMessage';
import { useStateWithLocalStorage } from '../../../utils';
import { useSteps } from './JoditEditor/useSteps';
import { handleActiveKey } from './JoditEditor/utils/handleActiveKey';
import { handleDisplaySaveButton } from './JoditEditor/utils/displaySaveButton';
import { normalizeTemplateContent } from './JoditEditor/utils/normalizeTableWidths';
import { useFaqCustomError } from './utils/useFaqCustomError';
import { subRoutes } from '../../../utils/constants/adminRoutes';

/**
 * The `DocEditor` component serves as a document editor, allowing users to create or edit template documents.
 * It provides functionalities such as loading the current template document details and configurations,
 * changing steps between form info and editor view, and handling document submission.
 *
 * @component
 * @param {Object} props - Properties passed down from parent component.
 * @param {string} props.purpose - Purpose of the document editor which can either be "create" or "edit".
 * @param {string} props.type - The type of template document.
 *
 * @example
 *
 * return <DocEditor purpose="create" />;
 * @returns {JSX.Element} A React component which contains the form and the editor for creating/editing template documents.
 */
export const DocEditor = ({ purpose, type }) => {
  const [form] = Form.useForm();
  const { dispatchAPI, daycare } = useAuthContext();
  const { message } = useErrorMessage();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const { verifyUsedOrderErrorFAQ } = useFaqCustomError();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [template, setTemplate] = useState();
  const [pickedConfiguration, setPickedConfiguration] = useState();
  const [step, setStep] = useState('config');
  const [tags, setTags] = useState([]);

  const [formValues, setFormValues] = useState({
    title: '',
    status: '',
    default_template: '',
    header: false,
    footer: false
  });
  const [content, setContent] = useState({
    header: '',
    body: '',
    footer: ''
  });
  // Shared by the header, body and footer editors, and remembered. The hook
  // swallows a persisted `false`, so the flag stored is "hidden".
  const [isKeysPanelHidden, setIsKeysPanelHidden] = useStateWithLocalStorage(
    'templateKeysPanelHidden',
    false
  );
  const toggleKeysPanel = useCallback(
    () => setIsKeysPanelHidden((prev) => !prev),
    []
  );

  const steps = useSteps(
    status,
    configurations,
    pickedConfiguration,
    setPickedConfiguration,
    template,
    content,
    setContent,
    purpose,
    isSubmitting,
    formValues,
    tags,
    t,
    type,
    { hidden: isKeysPanelHidden, toggle: toggleKeysPanel }
  );

  const getResource = useCallback(async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `template-documents/${id}`
      });
      setTemplate(data);
      setContent({
        header: data.header || '',
        body: data.body || '',
        footer: data.footer || ''
      });
      setFormValues({
        ...data,
        header: !!data?.header,
        footer: !!data?.footer
      });
      setPickedConfiguration(data.use_case);
      form.setFieldsValue({
        ...data,
        configuration: data.use_case
      });
    } catch (e) {
      message(e);
    }
  }, []);

  const fetchTags = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `template-documents/tags/${pickedConfiguration}?type=${type}`
      });
      setTags(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    if (pickedConfiguration) {
      (async () => {
        await fetchTags();
      })();
    }
  }, [pickedConfiguration]);

  const getEnums = useCallback(async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: '/template-documents/enums'
      });
      setConfigurations(data.configurations[type]);
      setStatus(data.status);
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (purpose === 'edit') {
      setIsLoading(true);
      if (!isLoading && configurations)
        (async () => {
          await getResource();
        })();
    }
  }, [getResource]);

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      await getEnums();
    })();
  }, []);

  const createTemplateDocument = async () => {
    setIsSubmitting(true);
    const normalizedContent = normalizeTemplateContent(content);
    try {
      const { data } = await dispatchAPI('POST', {
        url: 'template-documents',
        body: {
          ...formValues,
          type,
          use_case: pickedConfiguration,
          body: normalizedContent.body,
          header: normalizedContent.header,
          footer: normalizedContent.footer,
          daycare
        }
      });
      navigate(`${subRoutes.CONFIGURATIONS[type]}/show/${data?._id}`);
    } catch (e) {
      verifyUsedOrderErrorFAQ(e);
      setIsSubmitting(false);

      message(e);
    }
  };

  const updateTemplateDocument = async () => {
    setIsSubmitting(true);
    const normalizedContent = normalizeTemplateContent(content);

    try {
      await dispatchAPI('PATCH', {
        url: `template-documents/${id}`,
        body: {
          values: JSON.stringify({
            ...formValues,
            type,
            use_case: pickedConfiguration,
            body: normalizedContent.body,
            header: normalizedContent.header,
            footer: normalizedContent.footer,
            daycare
          })
        }
      });
      navigate(`${subRoutes.CONFIGURATIONS[type]}/show/${id}`);
    } catch (e) {
      verifyUsedOrderErrorFAQ(e);
      setIsSubmitting(false);
      message(e);
    }
  };

  const handleSubmit = async () => {
    if (purpose === 'edit') await updateTemplateDocument();
    if (purpose === 'create') await createTemplateDocument();
  };

  const displaySaveButton = handleDisplaySaveButton(step, steps);

  return (
    <>
      <PageHeaderCustom
        title={t(`template-documents.form.titles.${purpose}.${type}`, {
          title: t(`template-documents.form.${template?.use_case}`)
        })}
      />
      <ContentCustom transparent>
        <Spin spinning={isLoading}>
          <Form
            layout="vertical"
            form={form}
            onFinish={handleSubmit}
            onValuesChange={(_, allValues) => setFormValues(allValues)}
          >
            <Tabs
              className="doc-editor-tabs"
              items={steps}
              defaultActiveKey="config"
              activeKey={step}
              onChange={(key) => setStep(key)}
            />

            <Row className="form-footer">
              <Button
                icon={
                  step === 'config' && !displaySaveButton ? (
                    <CloseOutlined />
                  ) : (
                    <ArrowLeftOutlined />
                  )
                }
                onClick={() => {
                  if (step === 'config' && !displaySaveButton) {
                    return navigate(-1);
                  }
                  return handleActiveKey(steps, setStep, 'backward');
                }}
              >
                {step === 'config' && !displaySaveButton
                  ? t('buttons.cancel')
                  : t('buttons.previous_step')}
              </Button>
              {!displaySaveButton && (
                <Button
                  icon={<ArrowRightOutlined />}
                  iconPosition="end"
                  onClick={() => handleActiveKey(steps, setStep, 'forward')}
                  disabled={!pickedConfiguration}
                >
                  {t('buttons.next_step')}
                </Button>
              )}
              <Button
                type="add"
                htmlType="submit"
                icon={<CheckOutlined />}
                loading={isSubmitting}
              >
                {t('buttons.save')}
              </Button>
            </Row>
          </Form>
        </Spin>
      </ContentCustom>
    </>
  );
};

DocEditor.propTypes = {
  purpose: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired
};
